/**
 * COMPTABILITÉ — LES ENTRÉES D'UNE NUIT, TELLES QUE QUATRE ÉCRANS LES MONTRENT.
 *
 * L'étape « Accueil » de l'assistant (où la guestlist se saisit), le résumé
 * de l'étape « Nuit », la synthèse, et la validation d'une nuit sans clôture
 * détaillée posent la même question : combien de personnes sont entrées ?
 * Deux origines qui ne se confondent pas :
 *
 *   · les billets Shotgun, relevés tout seuls — VALIDES, tous canaux. Le
 *     nombre de scannés est un CONTRÔLE posé à côté, pas le total : un lecteur
 *     en panne une heure sous-compte la salle ;
 *   · la guestlist, que seule la feuille de l'Accueil connaît : elle se
 *     saisit. Pas encore saisie, elle se dit « à saisir » — jamais « 0 ».
 *
 * Le serveur fait le total ; l'écran ne recompte rien.
 *
 * ═══ LA GUESTLIST SE SAISIT À L'ACCUEIL (exploitant, 07/10/2026) ═══
 *
 * « Les guestlist à rentrer, il faudrait que ce soit dans la partie Accueil
 * plutôt. » La feuille vient du guichet : c'est à l'étape de l'Accueil, à
 * côté de sa caisse et de ses TPE, qu'on la recopie (`EntreesAccueil`).
 * L'étape « Nuit » n'en garde qu'un résumé en lecture, avec le chemin vers
 * l'Accueil (`ResumeEntrees`).
 */

import { useEffect, useState } from 'react'
import { ArrowRight, Users } from 'lucide-react'
import type { ClosingApi } from './ClosingCommon.js'
import './finance-entrees.css'

/**
 * Les entrées servies par l'API (`GET /closings/:id`, proposition de
 * validation). Les cinq premiers champs sont le contrat lu partout ; la
 * ventilation arrive en plus, et un écran plus ancien qui ne la recevrait pas
 * se tait sur elle plutôt que d'écrire des zéros.
 */
export interface EntreesNuit {
  shotgun_valid: number
  shotgun_scanned: number
  guestlist_count: number | null
  guestlist_note: string | null
  total: number
  shotgun_onsite?: number
  shotgun_presale?: number
  shotgun_free?: number
  /**
   * LES BILLETS DES ASSOCIATIONS (PRETIX) VENDUS — 16/10/2026. Les entrées
   * d'une nuit sont les billets vendus (la règle de la DAF) ; ceux des
   * associations s'ajoutent à Shotgun quand `associations_counted`.
   * Absents d'un serveur d'avant : la ligne se tait.
   */
  associations_sold?: number
  associations_scanned?: number
  associations_counted?: boolean
  associations_events?: Array<{ event_id: string; name: string; billetterie: string; sold: number; scanned: number }>
  /** Instant du gel (finalisation) ; `null` ou absent = relu en direct. */
  frozen_at?: string | null
}

/** La nuit a-t-elle des billets d'associations à montrer ? */
const avecAssociations = (e: EntreesNuit): boolean => (e.associations_sold ?? 0) > 0 || (e.associations_events?.length ?? 0) > 0

/** « dont 17 scannés · ASSO ALPHA 15 · ASSO BETA 5 » — la provenance, événement par événement. */
export function ventilationAssociations(e: EntreesNuit): string {
  const scannes = e.associations_scanned ?? 0
  const parts = [`${nombre(scannes)} ${scannes > 1 ? 'scannés' : 'scanné'}`]
  for (const x of e.associations_events ?? []) parts.push(`${x.name} ${nombre(x.sold)}`)
  return `dont ${parts.join(' · ')}`
}

/** « 1 234 » — un nombre de personnes, avec l'espace des milliers. */
export function nombre(n: number): string {
  return n.toLocaleString('fr-FR')
}

/** « 1 billet », « 12 billets ». */
export function compte(n: number, singulier: string, pluriel = `${singulier}s`): string {
  return `${nombre(n)} ${n > 1 ? pluriel : singulier}`
}

/** « dont 642 scannés · 468 au guichet · 174 en prévente · 128 exonérations » — ce qui est connu, rien d'autre. */
export function ventilationBillets(e: EntreesNuit): string {
  const parts = [`${nombre(e.shotgun_scanned)} ${e.shotgun_scanned > 1 ? 'scannés' : 'scanné'}`]
  if (typeof e.shotgun_onsite === 'number') parts.push(`${nombre(e.shotgun_onsite)} au guichet`)
  if (typeof e.shotgun_presale === 'number') parts.push(`${nombre(e.shotgun_presale)} en prévente`)
  if (typeof e.shotgun_free === 'number' && e.shotgun_free > 0) parts.push(compte(e.shotgun_free, 'exonération'))
  return `dont ${parts.join(' · ')}`
}

/** Un nombre connu, ou « — » : une ventilation absente n'est pas un zéro. */
const ouTiret = (n: number | undefined): string => (typeof n === 'number' ? nombre(n) : '—')

/**
 * LES ENTRÉES, LIGNE À LIGNE — billets, guestlist, total : ce qui est
 * ENREGISTRÉ. Une guestlist pas encore saisie s'écrit « À saisir ».
 *
 * `detaille` déplie la ventilation des billets en lignes propres (scannés,
 * guichet, préventes, exonérations) : c'est le récapitulatif de l'Accueil,
 * où l'on recopie la feuille et où chaque chiffre se compare à elle.
 */
export function ListeEntrees(props: { e: EntreesNuit; titreTotal?: string; detaille?: boolean }) {
  const { e } = props
  return (
    <dl className={`fe-liste${props.detaille ? ' fe-liste--detaillee' : ''}`}>
      <div className="fe-ligne">
        <dt>
          <span className="fe-ligne__lib">Billets Shotgun valides</span>
          <span className="fe-ligne__sous">
            {props.detaille ? 'Tous canaux ; les billets remboursés n’entrent pas.' : ventilationBillets(e)}
          </span>
        </dt>
        <dd className="fe-ligne__val">{nombre(e.shotgun_valid)}</dd>
      </div>
      {props.detaille ? (
        <>
          <div className="fe-ligne fe-ligne--sous">
            <dt>
              <span className="fe-ligne__lib">dont scannés à l’entrée</span>
              <span className="fe-ligne__sous">Un contrôle, pas le total : un lecteur en panne sous-compte la salle.</span>
            </dt>
            <dd className="fe-ligne__val">{nombre(e.shotgun_scanned)}</dd>
          </div>
          <div className="fe-ligne fe-ligne--sous">
            <dt><span className="fe-ligne__lib">dont vendus au guichet</span></dt>
            <dd className="fe-ligne__val">{ouTiret(e.shotgun_onsite)}</dd>
          </div>
          <div className="fe-ligne fe-ligne--sous">
            <dt><span className="fe-ligne__lib">dont préventes</span></dt>
            <dd className="fe-ligne__val">{ouTiret(e.shotgun_presale)}</dd>
          </div>
          <div className="fe-ligne fe-ligne--sous">
            <dt><span className="fe-ligne__lib">dont exonérations</span></dt>
            <dd className="fe-ligne__val">{ouTiret(e.shotgun_free)}</dd>
          </div>
        </>
      ) : null}
      {avecAssociations(e) ? (
        <div className={`fe-ligne${e.associations_counted === false ? ' fe-ligne--hors' : ''}`}>
          <dt>
            <span className="fe-ligne__lib">Billets d’associations vendus (Pretix)</span>
            <span className="fe-ligne__sous">
              {e.associations_counted === false
                ? `Hors du total (Comptabilité › Règles) · ${ventilationAssociations(e)}`
                : `Vendus, pas seulement scannés : la règle de la DAF · ${ventilationAssociations(e)}`}
            </span>
          </dt>
          <dd className="fe-ligne__val">{nombre(e.associations_sold ?? 0)}</dd>
        </div>
      ) : null}
      <div className="fe-ligne">
        <dt>
          <span className="fe-ligne__lib">Guestlist</span>
          <span className="fe-ligne__sous">
            {e.guestlist_note ?? 'Saisie à la main : la liste ne vient pas de Shotgun.'}
          </span>
        </dt>
        <dd className="fe-ligne__val">
          {e.guestlist_count === null ? <span className="fe-ligne__manque">À saisir</span> : nombre(e.guestlist_count)}
        </dd>
      </div>
      <div className="fe-ligne fe-ligne--total">
        <dt>
          <span className="fe-ligne__lib">{props.titreTotal ?? 'Total des entrées'}</span>
          {e.guestlist_count === null ? (
            <span className="fe-ligne__sous">Billets seuls, tant que la guestlist n’est pas saisie.</span>
          ) : null}
        </dt>
        <dd className="fe-ligne__val">{nombre(e.total)}</dd>
      </div>
    </dl>
  )
}

/* ═══════════════════════ l'Accueil : la guestlist se saisit ═══════════════════════ */

/** Un nombre de personnes saisi : un entier, ou « pas un nombre ». Vide = retirer la saisie. */
function lireGuestlist(saisie: string): number | null | 'illisible' {
  const t = saisie.trim()
  if (t === '') return null
  // Même borne que le serveur (`GUESTLIST_MAX`) : au-delà, un zéro de trop.
  return /^\d{1,4}$/.test(t) && Number(t) <= 5000 ? Number(t) : 'illisible'
}

/**
 * LES ENTRÉES DE LA NUIT, À L'ACCUEIL — Shotgun en automatique, la guestlist
 * en saisie, côte à côte quand la largeur le permet.
 *
 * La saisie reste LOCALE tant qu'elle n'est pas enregistrée ; les valeurs du
 * serveur ne l'écrasent que si personne n'est en train de taper (une autre
 * écriture de la clôture recharge le détail : elle ne doit pas effacer un
 * nombre à moitié saisi). Après un conflit de version, l'étape entière est
 * remontée par l'assistant (sa clé porte la graine) : la saisie refusée
 * repart de la version fraîche au lieu d'écraser celle de l'autre poste.
 */
export function EntreesAccueil(props: { api: ClosingApi; figee: boolean }) {
  const { api, figee } = props
  const d = api.closing
  const peutEditer = api.peut('finance.closure.edit') && !figee
  const e: EntreesNuit | null = d.entries ?? null
  const serveurCompte = e?.guestlist_count ?? null
  const serveurNote = e?.guestlist_note ?? null

  const [saisie, setSaisie] = useState(serveurCompte === null ? '' : String(serveurCompte))
  const [note, setNote] = useState(serveurNote ?? '')
  // « Touché » = quelqu'un a tapé ici depuis le dernier enregistrement. C'est
  // ce drapeau, et non une comparaison de valeurs, qui protège la saisie : une
  // guestlist changée depuis un autre poste doit remplacer un champ intact,
  // jamais un champ en cours de frappe.
  const [touche, setTouche] = useState(false)
  const [envoi, setEnvoi] = useState(false)
  const lu = lireGuestlist(saisie)
  const modifie = (lu === 'illisible' ? true : lu !== serveurCompte) || (note.trim() === '' ? null : note.trim()) !== serveurNote

  useEffect(() => {
    if (touche) return
    setSaisie(serveurCompte === null ? '' : String(serveurCompte))
    setNote(serveurNote ?? '')
    // Seules les valeurs du SERVEUR resynchronisent le champ.
  }, [serveurCompte, serveurNote]) // eslint-disable-line react-hooks/exhaustive-deps

  const enregistrer = async () => {
    if (lu === 'illisible' || !modifie) return
    setEnvoi(true)
    const passe = await api.ecrire('PUT', `/api/v1/finance/closings/${d.id}/guestlist`, {
      guestlist_count: lu, note: note.trim() === '' ? null : note.trim(),
    })
    setEnvoi(false)
    if (passe) setTouche(false)
  }

  return (
    <section className="lc-panel fe-accueil">
      <div className="lc-panel__head">
        <span className="fih-head__icon" aria-hidden="true"><Users size={17} /></span>
        <h2 className="lc-panel__title">Entrées de la nuit</h2>
        <span className="lc-panel__spacer" />
        {e ? (
          <span className={e.guestlist_count === null ? 'lc-pill lc-pill--warning' : 'lc-pill lc-pill--ok'}>
            {e.guestlist_count === null ? 'Guestlist à saisir' : compte(e.total, 'entrée')}
          </span>
        ) : null}
      </div>
      <div className="lc-panel__body">
        {e === null ? (
          <p className="fi-empty">Les entrées de cette nuit ne sont pas encore servies par le serveur.</p>
        ) : (
          <>
            <p className="lc-hint fe-intro">
              Les billets viennent de Shotgun, tout seuls. La guestlist n’y est pas : elle se compte sur la
              feuille de l’Accueil et se recopie ici. Le total est fait par Gestion{e.frozen_at ? ', et il est figé depuis la finalisation.' : ' ; la finalisation le fige.'}
            </p>
            {/* La liste dit ce qui est ENREGISTRÉ ; le formulaire, à côté, le modifie. */}
            <div className="fe-entrees">
              <ListeEntrees e={e} detaille />
              {peutEditer ? (
                <form
                  className="fe-guestlist"
                  onSubmit={(ev) => { ev.preventDefault(); void enregistrer() }}
                  aria-label="Saisir la guestlist"
                >
                  <p className="fe-guestlist__titre">Recopier la feuille de guestlist</p>
                  <div className="fe-guestlist__ligne">
                    <div className="fe-guestlist__champ">
                      <label className="lc-label" htmlFor={`fe-guestlist-${d.id}`}>Invités de la guestlist</label>
                      <input
                        id={`fe-guestlist-${d.id}`}
                        className={`lc-input fe-nombre${lu === 'illisible' ? ' lc-input--error' : ''}`}
                        inputMode="numeric"
                        value={saisie}
                        onChange={(ev) => { setSaisie(ev.target.value); setTouche(true) }}
                        aria-invalid={lu === 'illisible'}
                        placeholder="Nombre"
                      />
                    </div>
                    <div className="fe-guestlist__champ fe-guestlist__champ--note">
                      <label className="lc-label" htmlFor={`fe-guestlist-note-${d.id}`}>Note (facultative)</label>
                      <input
                        id={`fe-guestlist-note-${d.id}`}
                        className="lc-input fe-note"
                        value={note}
                        maxLength={200}
                        onChange={(ev) => { setNote(ev.target.value); setTouche(true) }}
                        placeholder="Par exemple : liste du DJ, 12 noms"
                      />
                    </div>
                    <button type="submit" className="lc-btn lc-btn--primary" disabled={envoi || !modifie || lu === 'illisible'}>
                      {envoi ? 'Enregistrement en cours' : 'Enregistrer la guestlist'}
                    </button>
                  </div>
                  <span className={lu === 'illisible' ? 'fe-guestlist__etat fe-guestlist__etat--erreur' : 'fe-guestlist__etat'}>
                    {lu === 'illisible'
                      ? 'Un nombre entier de personnes, de 0 à 5 000.'
                      : modifie
                        ? 'Pas encore enregistrée : le total ne la compte qu’une fois enregistrée.'
                        : e.guestlist_count === null
                          ? 'Saisissez le nombre d’invités, même 0 : « à saisir » ne veut pas dire « aucun invité ».'
                          : 'Enregistrée. Vider le champ puis enregistrer la remet « à saisir ».'}
                  </span>
                </form>
              ) : figee ? null : (
                <p className="lc-hint fe-note-role">Votre rôle ne permet pas de saisir la guestlist.</p>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  )
}

/* ═══════════════════════ la Nuit : un résumé, et le chemin ═══════════════════════ */

/**
 * LE RÉSUMÉ DES ENTRÉES, EN LECTURE — pour l'étape « Nuit ». Trois chiffres
 * (total, billets, guestlist) et le chemin vers l'Accueil, où la guestlist se
 * saisit. Le chemin est une ÉTAPE de l'assistant (`onAccueil`), jamais une
 * navigation : la clôture ne se recharge pas.
 *
 * Sans étape d'Accueil (le point de vente n'est pas dans le périmètre), le
 * résumé dit comment en avoir une plutôt que de laisser chercher.
 */
export function ResumeEntrees(props: { e: EntreesNuit | null; onAccueil: (() => void) | null; figee: boolean }) {
  const { e, onAccueil, figee } = props
  const manque = e !== null && e.guestlist_count === null
  return (
    <section className="lc-panel fe-resume">
      <div className="lc-panel__head">
        <span className="fih-head__icon" aria-hidden="true"><Users size={17} /></span>
        <h2 className="lc-panel__title">Entrées</h2>
        <span className="lc-panel__spacer" />
        {onAccueil ? (
          <button type="button" className={`lc-btn lc-btn--sm${manque && !figee ? ' lc-btn--primary' : ''}`} onClick={onAccueil}>
            {manque && !figee ? 'Saisir la guestlist à l’Accueil' : 'Voir le détail à l’Accueil'}
            <ArrowRight size={15} aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <div className="lc-panel__body">
        {e === null ? (
          <p className="lc-hint">Les entrées de cette nuit ne sont pas encore servies par le serveur.</p>
        ) : (
          <>
            <dl className="fe-resume__chiffres">
              <div className="fe-resume__chiffre fe-resume__chiffre--total">
                <dt>Total des entrées</dt>
                <dd>{nombre(e.total)}</dd>
              </div>
              <div className="fe-resume__chiffre">
                <dt>Billets Shotgun valides</dt>
                <dd>{nombre(e.shotgun_valid)}</dd>
              </div>
              <div className="fe-resume__chiffre">
                <dt>Scannés à l’entrée</dt>
                <dd>{nombre(e.shotgun_scanned)}</dd>
              </div>
              {avecAssociations(e) ? (
                <div className="fe-resume__chiffre">
                  <dt>Billets d’associations vendus</dt>
                  <dd>{nombre(e.associations_sold ?? 0)}</dd>
                </div>
              ) : null}
              <div className="fe-resume__chiffre">
                <dt>Guestlist</dt>
                <dd>{e.guestlist_count === null ? <span className="fe-ligne__manque">À saisir</span> : nombre(e.guestlist_count)}</dd>
              </div>
            </dl>
            <p className="lc-hint fe-resume__note">
              {onAccueil
                ? 'La guestlist se recopie à l’étape de l’Accueil, à côté de sa caisse : la feuille vient du guichet.'
                : 'La guestlist se recopie à l’étape de l’Accueil. L’Accueil ne fait pas partie de cette clôture : ajoutez-le au périmètre ci-dessous pour en avoir l’étape.'}
            </p>
          </>
        )}
      </div>
    </section>
  )
}
